import { createContext, useContext, useEffect, useState } from "react";
import {
  DEFAULT_THEME_COLOR,
  THEME_COLOR_STORAGE_KEY,
  applyThemeColor,
} from "constants/themeColors";

const SettingsContext = createContext(null);

// Apply persisted settings immediately on module load (before first render)
// so there's no flash of wrong theme/lang
const getStoredValue = (key, fallback) => {
  if (typeof localStorage === "undefined") return fallback;

  return localStorage.getItem(key) || fallback;
};

const savedTheme = getStoredValue("nf_theme", "light");
const savedLang = getStoredValue("nf_language", "bn");
const savedThemeColor = getStoredValue(
  THEME_COLOR_STORAGE_KEY,
  DEFAULT_THEME_COLOR,
);

if (typeof document !== "undefined") {
  if (savedTheme === "dark") document.documentElement.classList.add("dark");
  document.documentElement.setAttribute("lang", savedLang);
  applyThemeColor(savedThemeColor);
}

export const SettingsProvider = ({ children }) => {
  const [theme, setTheme] = useState(
    () => localStorage.getItem("nf_theme") || "light",
  );
  const [language, setLanguage] = useState(
    () => localStorage.getItem("nf_language") || "bn",
  );
  const [themeColor, setThemeColor] = useState(
    () => localStorage.getItem(THEME_COLOR_STORAGE_KEY) || DEFAULT_THEME_COLOR,
  );

  // Apply / remove the "dark" class on <html> whenever theme changes
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("nf_theme", theme);
  }, [theme]);

  useEffect(() => {
    applyThemeColor(themeColor);
    localStorage.setItem(THEME_COLOR_STORAGE_KEY, themeColor);
  }, [themeColor]);

  // Sync lang attribute on <html> and persist
  useEffect(() => {
    document.documentElement.setAttribute("lang", language);
    localStorage.setItem("nf_language", language);
  }, [language]);

  const toggleTheme = () =>
    setTheme((prev) => (prev === "light" ? "dark" : "light"));

  const changeLanguage = (lang) => setLanguage(lang);
  const changeThemeColor = (color) => setThemeColor(color);

  return (
    <SettingsContext.Provider
      value={{
        theme,
        toggleTheme,
        language,
        changeLanguage,
        themeColor,
        changeThemeColor,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error("useSettings must be used inside SettingsProvider");
  return ctx;
};
